import { ComponentType } from 'react';
import { lazyComponent } from './lazyComponent';

/**
 * Wraps React.lazy with retry logic for chunk loading failures.
 * After a deployment, old chunks may no longer exist. This retries
 * the import and forces a page reload as a last resort.
 *
 * `key` names the component for the prerendered pages (see lazyComponent).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  key: string,
  importFn: () => Promise<{ default: T }>,
  retries = 2,
  interval = 1500
) {
  return lazyComponent(key, () => retryImport(importFn, retries, interval));
}

async function retryImport<T>(
  importFn: () => Promise<T>,
  retries: number,
  interval: number
): Promise<T> {
  try {
    return await importFn();
  } catch (error) {
    if (retries <= 0) {
      // Last resort: check if we already tried reloading (browser only).
      if (typeof window !== 'undefined') {
        const hasReloaded = sessionStorage.getItem('chunk_reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload', '1');
          window.location.reload();
        }
      }
      throw error;
    }

    await new Promise(resolve => setTimeout(resolve, interval));
    return retryImport(importFn, retries - 1, interval);
  }
}
